import React, { useState, useEffect } from "react";
import { FeatherInfo } from "@subframe/core";
import {
  getPlSummaryInsights,
  type PlSummaryInsightItem,
} from "../../data/api/trialBalanceApi";

function dotClass(accentColor: string): string {
  switch (accentColor) {
    case "critical":
      return "bg-error-500";
    case "warning":
      return "bg-warning-500";
    default:
      return "bg-neutral-400";
  }
}

export function PlSummaryInsights() {
  const [insights, setInsights] = useState<PlSummaryInsightItem[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPlSummaryInsights()
      .then((res) => {
        if (!cancelled && res.success && res.data?.insights) {
          setInsights(res.data.insights);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || "Failed to load insights");
          setInsights([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex w-full flex-col items-start gap-4 rounded-lg border border-solid border-neutral-200 bg-default-background px-6 py-6">
      <div className="flex items-center gap-2">
        <div className="flex h-5 w-5 flex-none flex-col items-center justify-center rounded-full bg-neutral-200">
          <FeatherInfo className="text-caption font-caption text-neutral-600" />
        </div>
        <span className="text-body-bold font-body-bold text-neutral-900">
          P&amp;L Summary
        </span>
      </div>

      <div className="flex w-full flex-col items-start gap-3">
        {loading ? (
          <>
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex w-full items-start gap-3"
                aria-hidden
              >
                <div className="mt-1.5 h-2 w-2 flex-none rounded-full bg-neutral-200 animate-pulse" />
                <div
                  className="min-h-[1.25rem] flex-1 animate-pulse rounded bg-neutral-200"
                  style={{ maxWidth: "100%" }}
                />
              </div>
            ))}
          </>
        ) : error ? (
          <p className="text-body font-body text-neutral-600">{error}</p>
        ) : insights && insights.length > 0 ? (
          insights.map((item, index) => (
            <div key={index} className="flex w-full items-start gap-3">
              <div
                className={`mt-1.5 flex h-2 w-2 flex-none flex-col items-start rounded-full ${dotClass(item.accentColor)}`}
              />
              <span className="text-body font-body text-neutral-700">
                {item.description}
              </span>
            </div>
          ))
        ) : (
          <p className="text-body font-body text-neutral-600">
            No insights available.
          </p>
        )}
      </div>
    </div>
  );
}
